import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, User, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import config from '../../config';

const RegisterForm = ({ onSuccess, onSwitchToLogin }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Validation du mot de passe
  const passwordChecks = {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^a-zA-Z0-9]/.test(password)
  };

  const isPasswordValid = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isPasswordValid) {
      setError('Le mot de passe ne respecte pas les critères de sécurité');
      return;
    }

    if (!passwordsMatch) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${config.API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password, name: name || null })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Erreur lors de l\'inscription');
        return;
      }

      setSuccess(true);

      if (onSuccess) {
        onSuccess(data);
      }
    } catch (err) {
      console.error('Erreur inscription:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-success">
        <div className="success-icon">
          <CheckCircle size={48} />
        </div>
        <h3>Inscription réussie !</h3>
        <p>
          Un email de vérification a été envoyé à <strong>{email}</strong>.
          <br /><br />
          Veuillez cliquer sur le lien dans cet email pour activer votre compte.
        </p>
        <button onClick={onSwitchToLogin} className="auth-submit-btn">
          Retour à la connexion
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="auth-form">
      {error && (
        <div className="auth-error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      <div className="form-group">
        <div className="input-with-icon">
          <User size={18} className="input-icon" />
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Votre nom"
            autoComplete="name"
          />
        </div>
      </div>

      <div className="form-group">
        <div className="input-with-icon">
          <Mail size={18} className="input-icon" />
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="votre@email.com"
            required
            autoComplete="email"
          />
        </div>
      </div>

      <div className="form-group">
        <div className="input-with-icon">
          <Lock size={18} className="input-icon" />
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Créer un mot de passe"
            required
            autoComplete="new-password"
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        {password.length > 0 && (
          <div className="password-requirements">
            <div className={`requirement ${passwordChecks.length ? 'valid' : ''}`}>
              {passwordChecks.length ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
              Au moins 8 caractères
            </div>
            <div className={`requirement ${passwordChecks.lowercase ? 'valid' : ''}`}>
              {passwordChecks.lowercase ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
              Une lettre minuscule
            </div>
            <div className={`requirement ${passwordChecks.uppercase ? 'valid' : ''}`}>
              {passwordChecks.uppercase ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
              Une lettre majuscule
            </div>
            <div className={`requirement ${passwordChecks.number ? 'valid' : ''}`}>
              {passwordChecks.number ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
              Un chiffre
            </div>
            <div className={`requirement ${passwordChecks.special ? 'valid' : ''}`}>
              {passwordChecks.special ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
              Un caractère spécial (!@#$%...)
            </div>
          </div>
        )}
      </div>

      <div className="form-group">
        <div className="input-with-icon">
          <Lock size={18} className="input-icon" />
          <input
            type={showPassword ? 'text' : 'password'}
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirmer le mot de passe"
            required
            autoComplete="new-password"
          />
        </div>
        {confirmPassword.length > 0 && !passwordsMatch && (
          <div className="password-mismatch">
            <AlertCircle size={12} />
            Les mots de passe ne correspondent pas
          </div>
        )}
        {passwordsMatch && (
          <div className="password-match">
            <CheckCircle size={12} />
            Les mots de passe correspondent
          </div>
        )}
      </div>

      <button
        type="submit"
        className="auth-submit-btn"
        disabled={loading || !isPasswordValid || !passwordsMatch}
      >
        {loading ? (
          <>
            <Loader2 size={18} className="spin" />
            Inscription...
          </>
        ) : (
          'S\'inscrire'
        )}
      </button>

      <div className="auth-switch">
        <span>Déjà un compte ?</span>
        <button type="button" onClick={onSwitchToLogin}>
          Connectez-vous
        </button>
      </div>

      <p className="auth-legal">
        En continuant, vous acceptez nos <a href="/terms" target="_blank" rel="noopener noreferrer">Conditions d'utilisation</a> et notre <a href="/privacy" target="_blank" rel="noopener noreferrer">Politique de confidentialité</a>.
      </p>
    </form>
  );
};

export default RegisterForm;
